"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/helpers/tooltip"
import { useAnimationPreferences } from "@/user-preferences/use-animation-preferences"

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent !ring-offset-transparent ring-offset-1 disabled:text-muted-foreground tracking-wide disabled:!shadow-none",
    {
        variants: {
            variant: {
                default: `font-bold shadow bg-popover text-foreground border border-border-button shadow-popover
                    hover:text-accent focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:`,
                "default-data": `shadow bg-button text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                "input": `shadow-input bg-input text-foreground border border-border-input
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-input active:disabled:shadow`,

                accent: `
                relative isolate z-0 overflow-visible border-0 bg-transparent font-bold text-accent rounded-md
                    before:pointer-events-none before:absolute before:inset-0 before:z-[-1] before:rounded-md before:border-2 before:border-accent before:bg-background before:content-['']
                    before:[filter:drop-shadow(0px_4px_0px_var(--accent))]
                    transition-[transform,filter] duration-150 ease-out before:transition-[filter] before:duration-150 before:ease-out
                    hover:translate-y-[2px] hover:before:[filter:drop-shadow(0px_2px_0px_var(--accent))]
                    active:translate-y-[4px] active:before:[filter:none]
                    disabled:translate-y-0 disabled:text-muted-foreground disabled:before:border-border disabled:before:bg-neutral-200 disabled:before:[filter:none]
                    disabled:hover:translate-y-0 disabled:hover:before:[filter:none]
                    active:disabled:translate-y-0

                    after:absolute after:inset-0 after:z-[2] after:rounded-md after:bg-foreground after:opacity-0 hover:after:opacity-10
                    `,

                "accent-pill": `relative border bg-accent-50 hover:bg-accent-200 border-accent
                    text-foreground shadow-none font-bold
                    transition-colors duration-150 ease-out active:bg-accent-300
                    disabled:bg-neutral-200 disabled:text-muted-foreground disabled:hover:bg-neutral-200
                    !rounded`,
                "primary-foreground": `font-bold shadow bg-background text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                "primary-foreground-data": `shadow bg-background text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                secondary: `font-bold shadow bg-secondary text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                "secondary-data": `shadow bg-secondary text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                tertiary: `font-bold shadow bg-tertiary text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                "tertiary-data": `shadow bg-secondary text-foreground border border-border-button
                    hover:text-accent-foreground focus:border-ring focus:ring-purple-500
                    active:border-accent-foreground active:shadow-input-focus
                    active:disabled:border-border-button active:disabled:shadow`,
                report: `shadow font-bold border text-primary
                    hover:text-ticket focus:ring-purple-500
                    active:border-ticket
                    active:disabled:border-border active:disabled:shadow`,
                cancel: `shadow font-bold border text-tertiary-foreground bg-background border-none
                    hover:text-foreground focus:ring-purple-500
                    active:disabled:shadow`,
                "cancel-destructive": `shadow font-bold border text-tertiary-foreground bg-background border-none
                    hover:text-destructive focus:ring-purple-500
                    active:disabled:shadow`,
                outline: `font-bold shadow-none border border-border text-foreground
                    hover:text-accent focus:ring-purple-500
                    active:border-accent-foreground
                    active:disabled:border-border active:disabled:shadow-none`,
                "outline-data": `font-normal shadow-none border border-border text-foreground
                        hover:text-foreground hover:text-accent-foreground focus:ring-purple-500
                        active:border-accent-foreground
                        active:disabled:border-border active:disabled:shadow-none`,
                ghost: `font-bold shadow-none
                    hover:text-foreground focus:ring-purple-500
                   hover:bg-foreground/10
                    active:disabled:border active:disabled:shadow active:disabled:bg-transparent hover:disabled:bg-transparent`,
                link: `text-foreground underline-offset-4 shadow-none
                    hover:underline focus:underline focus:decoration-purple-500 focus:ring-transparent
                    active:text-accent-foreground active:decoration-accent-foreground
                    active:disabled:shadow active:disabled:decoration-transparent hover:disabled:decoration-transparent`,
                destructive: `font-bold text-destructive shadow-none
                    hover:underline focus:underline focus:decoration-purple-500 focus:ring-transparent
                    active:text-accent-foreground active:decoration-accent-foreground
                    active:disabled:shadow active:disabled:decoration-transparent hover:disabled:decoration-transparent`,
                "destructive-full": `font-bold shadow bg-destructive text-foreground border border-border-button
                        hover:text-destructive hover:bg-background-foreground focus:border-ring focus:ring-purple-500
                        active:border-accent-foreground active:shadow-input-focus
                        active:disabled:border-border-button active:disabled:`,
            },
            size: {
                default: "h-9 px-4 py-2 leading-9",
                xs: "h-5 rounded-md px-2 text-xs leading-5",
                sm: "h-7 rounded-md px-3 text-xs leading-7",
                lg: "h-10 rounded-md px-8",
                xl: "h-12 rounded-md px-4",
                icon: "min-h-9 min-w-9 max-w-9 max-h-9 flex flex-column justify-center items-center content-center",
                "table-action": "h-5 l-9 rounded-md flex flex-column justify-center items-center content-center",
                "icon-sm": "min-h-7 min-w-7 max-w-7 max-h-7 flex flex-column justify-center items-center content-center",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

/** When Settings → Animations disables movement, keep accent buttons at rest (no translate / filter deltas). */
const accent_variant_movement_off_class = cn(
    "!transition-none before:!transition-none !duration-0 before:!duration-0",
);

// * ------------------------------------ * //
export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
    message?: React.ReactNode
    /** When true, pointer may move to the label without closing; tooltip content is interactive. */
    messageHoverable?: boolean
    messageSide?: "left" | "right" | "top" | "bottom"
}

export type ButtonVariants = VariantProps<typeof buttonVariants>["variant"];

const Button = React.memo(
    React.forwardRef<HTMLButtonElement, ButtonProps>(
        ({ className, variant, size, message, messageHoverable = false, messageSide = "left", asChild = false, ...props }, ref) => {
            const Comp = asChild ? Slot : "button"
            const disablingRef = React.useRef({});

            const { animationPreferences } = useAnimationPreferences()
            const movement_animations_enabled =
                (animationPreferences?.enableAnimations ?? true) &&
                (animationPreferences?.enableMotion ?? false)

            const classNames = React.useMemo(() =>
                cn(
                    buttonVariants({ variant, size }),
                    variant === "accent" && !movement_animations_enabled ? accent_variant_movement_off_class : undefined,
                    className
                ),
                [variant, size, movement_animations_enabled, className]
            );

            if (message) {
                return (
                    <Tooltip delayDuration={0} disableHoverableContent={!messageHoverable}>
                        <TooltipTrigger asChild>
                            <Comp
                                className={classNames}
                                ref={ref}
                                {...props}
                            />
                        </TooltipTrigger>
                        <TooltipContent
                            side={messageSide}
                            className={messageHoverable ? "pointer-events-auto" : undefined}
                        >
                            {message}
                        </TooltipContent>
                    </Tooltip>
                )
            }
            else {
                return (
                    <Comp
                        className={classNames}
                        ref={ref}
                        {...props}
                    />
                )
            }
        }
    ),
    (prev, next) => {
        if (prev.className !== next.className) return false;
        if (prev.variant !== next.variant) return false;
        if (prev.size !== next.size) return false;
        if (prev.message !== next.message) return false;
        if (prev.messageHoverable !== next.messageHoverable) return false;
        if (prev.asChild !== next.asChild) return false;
        if (prev.ref !== next.ref) return false;
        if (prev.onClick !== next.onClick) return false;
        if (prev.onPointerDown !== next.onPointerDown) return false;
        if (prev.onPointerUp !== next.onPointerUp) return false;
        if (prev.onKeyDown !== next.onKeyDown) return false;
        if (prev.disabled !== next.disabled) return false;
        if (prev.messageSide !== next.messageSide) return false;
        return true;
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
